import GlobalStyles from '@styles/global';
import { SnackbarProvider } from 'notistack';
import { ThemeProvider, StyleSheetManager } from 'styled-components';
import {
  ThemeProvider as MuiThemeProvider,
  createTheme
} from '@mui/material/styles';
import { preventDefault } from '@utils/helpers';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { useEffect, useState } from 'react';
import { useInterfaceContext } from '@contexts/interfaceContext';
import { SidebarContextAPI } from '@contexts/sidebarContext';
import AuthLayout from 'AuthLayout';
import useAuth from '@hooks/useAuth';
import useWindowSize from '@hooks/useWindowSize';
import AppLayout from './AppLayout';

const App = () => {
  const page = document.documentElement;
  const { isAuthenticated } = useAuth();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const { isContrastMode, direction } = useInterfaceContext();
  const [navOpen, setNavOpen] = useState(false);
  const theme = createTheme({
    direction: direction
  });
  const cacheRtl = createCache({
    key: 'css-rtl',
    stylisPlugins: [rtlPlugin]
  });

  useEffect(() => {
    page.setAttribute('dir', direction);
  }, [direction, page]);

  useEffect(() => {
    isContrastMode && page.classList.add('contrast');
    preventDefault();
  }, [isContrastMode, page]);

  return (
    <CacheProvider value={cacheRtl}>
      <MuiThemeProvider theme={theme}>
        <ThemeProvider theme={{ theme: 'light' }}>
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{
              vertical: 'top',
              horizontal: direction === 'ltr' ? 'right' : 'left'
            }}
            autoHideDuration={3000}
          >
            <SidebarContextAPI>
              <GlobalStyles navOpen={navOpen} isMobile={isMobile} />
              <StyleSheetManager
                stylisPlugins={direction === 'rtl' ? [rtlPlugin] : []}
              >
                {isAuthenticated ? (
                  <AppLayout open={navOpen} setOpen={setNavOpen} />
                ) : (
                  <AuthLayout />
                )}
              </StyleSheetManager>
            </SidebarContextAPI>
          </SnackbarProvider>
        </ThemeProvider>
      </MuiThemeProvider>
    </CacheProvider>
  );
};

export default App;
